import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import DateFilterDropdown from '../components/DateFilterDropdown';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import {
  ArrowDropDown,
  Check,
  TrendingUp,
  Restaurant,
  AccountBalance,
  Assessment
} from '@mui/icons-material';
import { PageHeroBand } from '../components/ui';

// Import report components
import SalesDashboard from '../components/reports/SalesDashboard';
import MenuPerformance from '../components/reports/MenuPerformance';
import FinancialSummary from '../components/reports/FinancialSummary';
import OrderAnalytics from '../components/reports/OrderAnalytics';
import KitchenPerformanceReport from '../components/reports/KitchenPerformance';
import { toLocalIsoDate } from '../utils/dateLocal';

type ReportType = 'sales-dashboard' | 'menu-performance' | 'financial-summary' | 'order-analytics' | 'kitchen-performance';

const VendorReports: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [selectedReport, setSelectedReport] = useState<ReportType>('sales-dashboard');
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const reportOptions = [
    {
      value: 'sales-dashboard' as ReportType,
      label: 'Sales Dashboard & Overview',
      icon: <TrendingUp sx={{ fontSize: '1.1rem' }} />,
      description: 'Revenue, orders, and trends'
    },
    {
      value: 'menu-performance' as ReportType,
      label: 'Menu Performance Report',
      icon: <Restaurant sx={{ fontSize: '1.1rem' }} />,
      description: 'Top sellers and item analytics'
    },
    {
      value: 'financial-summary' as ReportType,
      label: 'Financial Summary Report',
      icon: <AccountBalance sx={{ fontSize: '1.1rem' }} />,
      description: 'Revenue, taxes, and payments'
    },
    {
      value: 'order-analytics' as ReportType,
      label: 'Order Analytics',
      icon: <Assessment sx={{ fontSize: '1.1rem' }} />,
      description: 'Order counts and customer insights'
    },
    {
      value: 'kitchen-performance' as ReportType,
      label: 'Kitchen & Table Performance',
      icon: <Restaurant sx={{ fontSize: '1.1rem' }} />,
      description: 'KOT timing, bottlenecks, table turnover'
    }
  ];

  const selectedOption = reportOptions.find(opt => opt.value === selectedReport) || reportOptions[0];

  // Calculate date range based on filter
  const getDateRange = () => {
    const today = new Date();
    const todayStr = toLocalIsoDate(today);

    // Check if it's a custom date range (format: YYYY-MM-DD,YYYY-MM-DD)
    if (dateFilter.includes(',')) {
      const [customStart, customEnd] = dateFilter.split(',');
      return { startDate: customStart, endDate: customEnd };
    }

    let startDate: string;
    let endDate: string = todayStr;

    switch (dateFilter) {
      case 'today':
        startDate = todayStr;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = toLocalIsoDate(yesterday);
        endDate = startDate; // Yesterday only
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = toLocalIsoDate(weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        startDate = toLocalIsoDate(monthAgo);
        break;
      case 'recent':
        const twoDaysAgo = new Date(today);
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 1);
        startDate = toLocalIsoDate(twoDaysAgo);
        break;
      case 'all':
        // For "all time", go back 1 year
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        startDate = toLocalIsoDate(yearAgo);
        break;
      default:
        startDate = todayStr;
    }

    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectReport = (reportType: ReportType) => {
    setSelectedReport(reportType);
    handleClose();
  };

  const renderReport = () => {
    if (!user?.id) {
      return (
        <Box
          sx={{
            p: '2rem',
            textAlign: 'center',
            color: theme.gray[600],
          }}
        >
          Please log in to view reports
        </Box>
      );
    }

    const props = {
      vendorId: user.id,
      startDate,
      endDate
    };

    switch (selectedReport) {
      case 'sales-dashboard':
        return <SalesDashboard {...props} />;
      case 'menu-performance':
        return <MenuPerformance {...props} />;
      case 'financial-summary':
        return <FinancialSummary {...props} />;
      case 'order-analytics':
        return <OrderAnalytics {...props} />;
      case 'kitchen-performance':
        return <KitchenPerformanceReport {...props} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        p: '16px 16px 32px',
        backgroundColor: theme.background,
        minHeight: '100vh',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <PageHeroBand eyebrow="Insights" title="Reports & Analytics" data-testid="reports-page-header" />

      {/* Filter Bar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
          mb: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {/* Report Type Dropdown */}
        <Button
          onClick={handleClick}
          variant="text"
          size="small"
          data-testid="report-type-dropdown"
          endIcon={<ArrowDropDown sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: theme.gray[500] }} />}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            color: theme.text,
            border: `1px solid ${theme.components?.input?.border || '#D0D0D0'}`,
            boxShadow: 'none',
            minWidth: { xs: 140, sm: 180 },
            height: { xs: 28, sm: 32 },
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            fontWeight: 600,
            padding: { xs: '2px 8px', sm: '4px 12px' },
            '&:hover': {
              backgroundColor: theme.components?.card?.hover || '#F8F8F8',
              borderColor: theme.gray[300],
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            },
            transition: 'all 0.15s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.4, sm: 0.6 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: theme.primary }}>
              {selectedOption.icon}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.text,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                lineHeight: 1
              }}
            >
              {selectedOption.label}
            </Typography>
          </Box>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: { xs: 220, sm: 280 },
              maxWidth: { xs: 300, sm: 350 },
              borderRadius: '8px',
              border: `1px solid ${theme.components?.card?.border || '#C0C0C0'}`,
              backgroundColor: theme.components?.modal?.background || '#FFFFFF',
              boxShadow: theme.components?.card?.shadow || '0 4px 12px rgba(0, 0, 0, 0.12)',
              overflow: 'visible',
              '& .MuiMenuItem-root': {
                borderRadius: '6px',
                mx: 0.5,
                my: 0.15,
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                padding: { xs: '8px 10px', sm: '10px 12px' },
                minHeight: 'unset',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: theme.components?.card?.hover || '#F5F5F5',
                  transform: 'translateX(2px)',
                },
                '&.Mui-selected': {
                  backgroundColor: theme.gray[200],
                  '&:hover': {
                    backgroundColor: theme.gray[300],
                  }
                }
              },
              '& .MuiList-root': {
                padding: { xs: '4px', sm: '6px' },
              }
            }
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          {reportOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={selectedReport === option.value}
              onClick={() => handleSelectReport(option.value)}
              sx={{
                py: { xs: 0.6, sm: 0.8 },
                px: { xs: 1, sm: 1.2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: { xs: 0.8, sm: 1 } }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: selectedReport === option.value ? theme.primary : theme.gray[500],
                  pt: 0.2
                }}>
                  {option.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: selectedReport === option.value ? 600 : 500,
                      color: theme.text,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      lineHeight: 1.3,
                      mb: 0.2
                    }}
                  >
                    {option.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.gray[500],
                      fontSize: { xs: '0.65rem', sm: '0.72rem' },
                      lineHeight: 1.2
                    }}
                  >
                    {option.description}
                  </Typography>
                </Box>
                {selectedReport === option.value && (
                  <Check
                    fontSize="small"
                    sx={{
                      color: theme.success,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      mt: 0.5
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        {/* Date Filter Dropdown */}
        <DateFilterDropdown
          value={dateFilter}
          onChange={setDateFilter}
          showAllOption={true}
        />
      </Box>

      {/* Report Content */}
      <Box>
        {renderReport()}
      </Box>
    </Box>
  );
};

export default VendorReports;
